import {
  fromNumericKind,
  matchTrackKind,
  questionFor,
  toNumericKind,
  type BooleanKind,
  type EnumKind,
  type NumericKind,
  type ScaleConfig,
  type Track,
  type TrackKind,
  type TrackKindName,
  type ValidationError,
  type ValueWord,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { TextArea, TextField } from '../../components/Field.js'
import { useToast } from '../../components/Toast.js'
import { ValueInput } from '../../components/value-input/ValueInput.js'
import { useServices } from '../../services/ServicesProvider.js'
import { UnsavedWorkGuard, useDiscardGuard } from '../../shell/DiscardGuard.js'
import { EnumKindEditor } from './EnumKindEditor.js'
import { NumericKindEditor } from './NumericKindEditor.js'
import { ValueWordsEditor } from './ValueWordsEditor.js'
import styles from './TrackEditor.module.css'

const DEFAULT_NUMERIC: NumericKind = {
  kind: 'numeric',
  min: 1,
  max: 10,
  step: 1,
  decimals: 0,
}
const DEFAULT_BOOLEAN: BooleanKind = {
  kind: 'boolean',
  trueLabel: 'Yes',
  falseLabel: 'No',
}

const KIND_NAMES: { readonly id: TrackKindName; readonly label: string }[] = [
  { id: 'numeric', label: 'A number' },
  { id: 'enum', label: 'A set of options' },
  { id: 'boolean', label: 'Yes or no' },
]

/**
 * Creating and configuring a track (T-302).
 *
 * Everything that decides whether an edit is *safe* lives in core —
 * `planUpdate` reads the measurements and reports what would break, and
 * `applyEdit` refuses rather than trusting the form. This screen's job is to
 * ask before the user gets that far, so a blocked save is a rarity rather than
 * the normal way to discover a rule.
 */
export function TrackEditorScreen() {
  const { repos, ids } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const params = useParams()
  const trackId = params['id']
  const isNew = trackId === undefined || trackId === 'new'

  const [loaded, setLoaded] = useState(isNew)
  const [existing, setExisting] = useState<Track | null>(null)
  const [measurementCount, setMeasurementCount] = useState(0)
  const [recordedOptionIds, setRecordedOptionIds] = useState<ReadonlySet<string>>(
    new Set(),
  )

  const [name, setName] = useState('')
  const [unit, setUnit] = useState('')
  const [description, setDescription] = useState('')
  const [question, setQuestion] = useState('')
  const [kind, setKind] = useState<TrackKind>(DEFAULT_NUMERIC)
  const [scale, setScale] = useState<ScaleConfig>(fromNumericKind(DEFAULT_NUMERIC))
  const [words, setWords] = useState<readonly ValueWord[]>([])
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [dirty, setDirty] = useState(false)
  const [preview, setPreview] = useState<number | boolean | string | null>(null)

  // One flag for the update-prompt deferral, the indicator and the discard
  // confirmation (T-204, T-808).
  const guard = useDiscardGuard(dirty)

  useEffect(() => {
    if (isNew) {
      return
    }
    let cancelled = false
    async function load() {
      const track = await repos.tracks.get(trackId!)
      const entries = await repos.entries.listByTrack(trackId!)
      if (cancelled || !track) {
        return
      }
      const measurements = entries.flatMap((entry) =>
        entry.measurements.filter((m) => m.trackId === trackId),
      )
      setExisting(track)
      setName(track.name)
      setUnit(track.unit ?? '')
      setDescription(track.description ?? '')
      setQuestion(track.question ?? '')
      setKind(track.kind)
      if (track.kind.kind === 'numeric') {
        setScale(fromNumericKind(track.kind))
        setWords(track.kind.words ?? [])
      }
      setMeasurementCount(measurements.length)
      setRecordedOptionIds(
        new Set(
          measurements
            .map((m) => m.value)
            .filter((value): value is string => typeof value === 'string'),
        ),
      )
      setLoaded(true)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [isNew, trackId, repos])

  /**
   * The kind as currently configured, with the numeric scale folded back in.
   *
   * Words ride alongside the scale rather than inside `ScaleConfig`: the scale
   * is min/max/step/decimals and the derivation rules that connect them, and a
   * list of labels has nothing to do with any of that.
   */
  const currentKind: TrackKind =
    kind.kind === 'numeric'
      ? {
          ...toNumericKind(scale),
          // Empty means no words at all, not an empty list — the same rule the
          // model uses, so a track that never had any stays that way.
          ...(words.length === 0 ? {} : { words }),
        }
      : kind

  const previewTrack: Track = {
    id: existing?.id ?? 'preview',
    name: name.trim() || 'Untitled',
    ...(unit.trim() === '' ? {} : { unit: unit.trim() }),
    ...(question.trim() === '' ? {} : { question: question.trim() }),
    kind: currentKind,
    archived: false,
    position: existing?.position ?? 0,
    createdAt: existing?.createdAt ?? { epochMs: 0, timeZone: 'UTC' },
    updatedAt: existing?.updatedAt ?? { epochMs: 0, timeZone: 'UTC' },
  }

  /*
   * What an empty field will actually produce, asked of core rather than
   * rebuilt here. Passing a blank question is the same thing the model treats
   * as "no question", so the placeholder cannot drift from the derivation.
   */
  const derivedQuestion = questionFor({ ...previewTrack, question: '' })

  const changeKind = useCallback(
    (next: TrackKindName) => {
      setDirty(true)
      setPreview(null)
      if (next === 'numeric') {
        setScale(fromNumericKind(DEFAULT_NUMERIC))
        setKind(DEFAULT_NUMERIC)
      } else if (next === 'boolean') {
        setKind(DEFAULT_BOOLEAN)
      } else {
        setKind({
          kind: 'enum',
          ordered: false,
          options: [
            { id: ids.next(), label: '', score: null },
            { id: ids.next(), label: '', score: null },
          ],
        })
      }
    },
    [ids],
  )

  async function save() {
    setErrors([])
    const changes = {
      name: name.trim(),
      kind: currentKind,
      ...(unit.trim() === '' ? { unit: undefined } : { unit: unit.trim() }),
      ...(description.trim() === ''
        ? { description: undefined }
        : { description: description.trim() }),
      // Cleared means "derive it again", not "ask an empty question", so this
      // sends `undefined` and the field goes away rather than holding ''.
      ...(question.trim() === ''
        ? { question: undefined }
        : { question: question.trim() }),
    }

    const result = isNew
      ? await repos.tracks.create({
          name: changes.name,
          kind: currentKind,
          ...(unit.trim() === '' ? {} : { unit: unit.trim() }),
          ...(description.trim() === '' ? {} : { description: description.trim() }),
          ...(question.trim() === '' ? {} : { question: question.trim() }),
        })
      : // applyEdit, not update: it re-checks the history guards server-side of
        // this form, so a stale page cannot slip a damaging change past them.
        await repos.tracks.applyEdit(existing!.id, changes)

    if (!result.ok) {
      setErrors(result.errors)
      return
    }
    setDirty(false)
    // Leaving on purpose, so the guard stands aside. Without this it blocks the
    // app's own post-save navigation: `setDirty(false)` and `navigate()` run in
    // the same tick and the re-render has not happened yet.
    guard.allowLeaving()
    show({ message: isNew ? `Created ${result.value.name}.` : 'Saved.' })
    void navigate('/tracks')
  }

  if (!loaded) {
    return (
      <>
        <h1>Track</h1>
        <p>Loading…</p>
      </>
    )
  }

  const typeLocked = measurementCount > 0

  return (
    <>
      <h1>{isNew ? 'New track' : name || 'Track'}</h1>
      <UnsavedWorkGuard guard={guard} what={isNew ? 'This new track' : 'This track'} />

      <div className={styles['form']}>
        <section className={styles['section']}>
          <TextField
            label="Name"
            value={name}
            placeholder="What are you measuring?"
            onChange={(event) => {
              setName(event.target.value)
              setDirty(true)
            }}
          />
          {/*
            The derived default lives in the **placeholder**, not the value
            (T-810). Leaving this blank means "ask `${name}?`", and the
            placeholder shows exactly what that will be as the name is typed.

            The prototype copies the derived question into the field on create.
            That version has a bug it cannot see: rename the track and the
            question still asks the old name, so a track called Sleep spends the
            rest of its life asking about Kip. Deriving on read fixes it for
            free and makes "blank" a state rather than an omission.
          */}
          <TextField
            label="The question you'll be asked"
            optional
            value={question}
            placeholder={derivedQuestion}
            onChange={(event) => {
              setQuestion(event.target.value)
              setDirty(true)
            }}
          />
          <TextField
            label="Unit"
            optional
            value={unit}
            placeholder="kg, hours, cups…"
            onChange={(event) => {
              setUnit(event.target.value)
              setDirty(true)
            }}
          />
          <TextArea
            label="Description"
            optional
            value={description}
            placeholder="A note to your future self about what this means."
            onChange={(event) => {
              setDescription(event.target.value)
              setDirty(true)
            }}
          />
        </section>

        <section className={styles['section']}>
          <h2 className={styles['sectionTitle']}>What kind of value</h2>
          <div className={styles['kinds']}>
            {KIND_NAMES.map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={typeLocked && option.id !== currentKind.kind}
                className={`${styles['kindOption']} ${option.id === currentKind.kind ? styles['kindSelected'] : ''}`}
                onClick={() => changeKind(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/*
            Stated, not merely disabled. A control that silently does nothing
            reads as a bug; one that says why reads as a rule.
          */}
          {typeLocked && (
            <p className={styles['locked']}>
              This track already has{' '}
              {measurementCount === 1
                ? '1 measurement'
                : `${String(measurementCount)} measurements`}
              , so its value type is fixed — an existing measurement cannot be converted
              into a different kind of value without changing what was recorded. Create a
              new track if you need a different type.
            </p>
          )}

          {matchTrackKind(currentKind, {
            numeric: () => (
              <>
                <NumericKindEditor
                  scale={scale}
                  onChange={(next) => {
                    setScale(next)
                    setDirty(true)
                  }}
                />
                <ValueWordsEditor
                  scale={scale}
                  words={words}
                  onChange={(next) => {
                    setWords(next)
                    setDirty(true)
                  }}
                />
              </>
            ),
            boolean: (booleanKind) => (
              <div className={styles['pair']}>
                <TextField
                  label="Label for yes"
                  value={booleanKind.trueLabel}
                  onChange={(event) => {
                    setKind({ ...booleanKind, trueLabel: event.target.value })
                    setDirty(true)
                  }}
                />
                <TextField
                  label="Label for no"
                  value={booleanKind.falseLabel}
                  onChange={(event) => {
                    setKind({ ...booleanKind, falseLabel: event.target.value })
                    setDirty(true)
                  }}
                />
              </div>
            ),
            enum: (enumKind: EnumKind) => (
              <EnumKindEditor
                kind={enumKind}
                recordedOptionIds={recordedOptionIds}
                newOptionId={() => ids.next()}
                onChange={(next) => {
                  setKind(next)
                  setDirty(true)
                }}
              />
            ),
          })}
        </section>

        {/*
          The real control, not a picture of one. Seeing the thing you will
          tap dozens of times a week is how you notice a 200-position slider
          before you have logged against it for a month.
        */}
        <section className={styles['preview']}>
          <p className={styles['previewLabel']}>This is what logging will look like</p>
          <ValueInput track={previewTrack} value={preview} onChange={setPreview} />
        </section>

        {errors.length > 0 && (
          <div className={styles['errors']} role="alert">
            {errors.map((error) => (
              <span key={error.code} className={styles['error']}>
                {error.message}
              </span>
            ))}
          </div>
        )}

        <div className={styles['actions']}>
          <Button onClick={() => void navigate('/tracks')}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()}>
            {isNew ? 'Create track' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  )
}
